"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { AuctionItem } from "@/components/ui/AuctionCard";

interface CategoryBannerCarouselProps {
  auctions: AuctionItem[];
}

export function CategoryBannerCarousel({ auctions }: CategoryBannerCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto slide
  useEffect(() => {
    if (!auctions.length) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % auctions.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [auctions.length]);

  if (!auctions.length) return null;

  const auction = auctions[currentIndex];

  const formatTimeLeft = (timeLeft: string) => {
    if (!timeLeft) return "Ended";
    const diff = new Date(timeLeft).getTime() - Date.now();
    if (diff <= 0) return "Ended";

    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    return `${days}D ${hours}H ${minutes}M`;
  };

  return (
    <div className="relative w-full mb-20">
      {/* STABLE FEATURED STAGE */}
      <div className="relative min-h-[540px] rounded-[20px] overflow-hidden bg-gradient-to-br from-[#F3F6FF] to-[#ECEBFF]">

        {/* CLICKABLE IMAGE (FIXED WIDTH) */}
        <Link
          href={`/auctions/${auction.id}`}
          className="absolute right-0 top-0 bottom-0 w-[48%] cursor-pointer"
          aria-label="View auction details"
        >
          <Image
            src={auction.image}
            alt={auction.title}
            fill
            priority
            className="object-cover"
          />
        </Link>

        {/* CONTENT COLUMN */}
        <div className="relative z-10 h-full max-w-[58%] px-12 py-12 flex flex-col">

          {/* TOP FLEX ZONE (CLAMPED) */}
          <div className="flex-shrink-0 h-[260px] overflow-hidden">
            <span className="inline-block mb-4 px-4 py-1.5 rounded-full text-xs font-semibold bg-blue-600 text-white">
              Featured auction
            </span>

            <h2 className="text-5xl font-extrabold text-gray-900 leading-tight line-clamp-2">
              {auction.title}
            </h2>

            {auction.product_heromsg && (
              <p className="mt-4 text-lg text-gray-600 line-clamp-3">
                {auction.product_heromsg}
              </p>
            )}
          </div>

                  {/* BOTTOM FIXED ZONE (ANCHOR) */}
                 <div className="mt-auto flex items-end justify-between max-w-xl">
            <div>
              <div className="text-sm text-gray-500 mb-1">Current bid</div>
              <div className="text-4xl font-bold text-blue-600">
                {auction.currency}{" "}
                {auction.currentBid?.toLocaleString() ||
                  auction.startingBid?.toLocaleString()}
              </div>

              {auction.timeLeft && (
                <div className="text-sm text-gray-600 mt-2 mb-4">
                  Ends in{" "}
                  <span className="font-semibold">
                    {formatTimeLeft(auction.timeLeft)}
                  </span>
                </div>
              )}
              
            <Link
              href={`/auctions/${auction.id}`}
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-blue-600 text-white font-semibold
              hover:bg-blue-700 transition-all shadow-lg"
            >
              Bid now →
            </Link>
            </div>

          </div>
        </div>
      </div>

      {/* NAVIGATION CONTROLS (OUTSIDE CARD) */}
      <button
        onClick={() =>
          setCurrentIndex((i) => (i - 1 + auctions.length) % auctions.length)
        }
        className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-white/40 p-3 rounded-full shadow-md hover:shadow-lg transition"
        aria-label="Previous auction"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>

      <button
        onClick={() =>
          setCurrentIndex((i) => (i + 1) % auctions.length)
        }
        className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-white/40 p-3 rounded-full shadow-md hover:shadow-lg transition"
        aria-label="Next auction"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* DOT INDICATORS */}
      <div className="flex justify-center gap-2 mt-6">
        {auctions.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrentIndex(i)}
            className={`h-2 rounded-full transition-all duration-300 ${
              i === currentIndex ? "w-8 bg-blue-600" : "w-2 bg-gray-300"
            }`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
